import { supabase } from '../supabase/client';
import type { Session, User } from '@supabase/supabase-js';
import { generateUsername } from '../username-utils';
import { normalizePhoneE164 } from '../phone/normalize-phone';

/**
 * Generate a user profile slug (mirrors DB generate_slug function)
 * Format: {name-portion}-{5-char-id-suffix}
 */
function generateUserSlug(name: string, userId: string): string {
  const idClean = userId.replace(/-/g, '');
  const suffix = idClean.slice(-5).toLowerCase();

  if (!name || !name.trim()) return suffix;

  const base = name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);

  return base ? `${base}-${suffix}` : suffix;
}

export interface AuthResult {
  user: User | null;
  session: Session | null;
  error: Error | null;
}

/** Country chosen in the phone-number picker. */
export interface PhoneOtpCountry {
  id?: number | string | null;
  code?: string | null;
}

/**
 * Send OTP to phone number
 * Uses Supabase native auth endpoint: POST /auth/v1/otp
 * OTP logins default to persistent sessions (rememberMe = true)
 * @param captchaToken Cloudflare Turnstile token when Auth CAPTCHA is enabled
 * @param country Selected country, forwarded as signup metadata. On a new
 * signup the DB creates the profile row the moment the phone is confirmed, so
 * without this the country is inferred from the dial code — which cannot tell
 * the US from Canada. Supabase only applies `options.data` when creating a user,
 * so this is ignored for returning users.
 */
export async function sendPhoneOTP(
  phone: string,
  captchaToken?: string,
  country?: PhoneOtpCountry,
  otpOptions?: { shouldCreateUser?: boolean },
): Promise<{ error: Error | null }> {
  try {

    // Set rememberMe to true for OTP logins (default behavior)
    localStorage.setItem('candle-remember-me', 'true');

    const e164 = normalizePhoneE164(phone);
    if (!e164) {
      return { error: new Error('invalid phone') };
    }

    const data: Record<string, string> = {};
    if (country?.id != null && String(country.id) !== '') {
      data.country_id = String(country.id);
    }
    if (country?.code) {
      data.country_code = country.code;
    }

    const { error } = await supabase.auth.signInWithOtp({
      phone: e164,
      options: {
        channel: 'sms',
        shouldCreateUser: otpOptions?.shouldCreateUser ?? true,
        ...(captchaToken ? { captchaToken } : {}),
        ...(Object.keys(data).length > 0 ? { data } : {}),
      },
    });

    if (error) {
      console.error("[auth] sendPhoneOTP error:", error.message);
    }

    return { error };
  } catch (error) {
    console.error("[auth] sendPhoneOTP unexpected:", error);
    return { error: error as Error };
  }
}

/**
 * Send OTP to email
 * Uses Supabase native auth endpoint: POST /auth/v1/otp
 * OTP logins default to persistent sessions (rememberMe = true)
 */
export async function sendEmailOTP(
  email: string,
  captchaToken?: string,
): Promise<{ error: Error | null }> {
  try {
    // Set rememberMe to true for OTP logins (default behavior)
    localStorage.setItem('candle-remember-me', 'true');

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: captchaToken ? { captchaToken } : undefined,
    });

    return { error };
  } catch (error) {
    return { error: error as Error };
  }
}

/**
 * Verify phone OTP
 * Uses Supabase native auth endpoint: POST /auth/v1/verify
 */
export async function verifyPhoneOTP(phone: string, token: string): Promise<AuthResult> {
  try {
    const { data, error } = await supabase.auth.verifyOtp({
      phone,
      token,
      type: 'sms',
    });

    if (error) {
      return { user: null, session: null, error };
    }

    return {
      user: data.user,
      session: data.session,
      error: null,
    };
  } catch (error) {
    return { user: null, session: null, error: error as Error };
  }
}

/**
 * Verify email OTP
 * Uses Supabase native auth endpoint: POST /auth/v1/verify
 */
export async function verifyEmailOTP(email: string, token: string): Promise<AuthResult> {
  try {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });

    if (error) {
      return { user: null, session: null, error };
    }

    return {
      user: data.user,
      session: data.session,
      error: null,
    };
  } catch (error) {
    return { user: null, session: null, error: error as Error };
  }
}

/**
 * Sign up with email and password
 * New accounts default to persistent sessions (rememberMe = true)
 */
export async function signUpWithEmail(
  email: string,
  password: string,
  fullname: string,
  captchaToken?: string
): Promise<AuthResult> {
  try {
    // Set rememberMe to true for new sign-ups (default behavior)
    localStorage.setItem('candle-remember-me', 'true');

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          fullname,
        },
        ...(captchaToken ? { captchaToken } : {}),
      },
    });

    if (error) {
      return { user: null, session: null, error };
    }

    // Create user profile
    if (data.user) {
      await supabase.from('user_profile').insert({
        user_id: data.user.id,
        email,
        fullname,
        user_name: generateUsername(fullname),
        slug: generateUserSlug(fullname, data.user.id),
      });
    }

    return {
      user: data.user,
      session: data.session,
      error: null,
    };
  } catch (error) {
    return { user: null, session: null, error: error as Error };
  }
}

/**
 * Sign in with email and password
 * @param email - User's email
 * @param password - User's password
 * @param rememberMe - If true, session persists indefinitely. If false, session expires when browser closes.
 */
export async function signInWithEmail(
  email: string,
  password: string,
  rememberMe: boolean = true,
  captchaToken?: string
): Promise<AuthResult> {
  try {
    // Store the rememberMe preference
    if (rememberMe) {
      localStorage.setItem('candle-remember-me', 'true');
    } else {
      localStorage.setItem('candle-remember-me', 'false');
      // For non-persistent sessions, we'll use sessionStorage
      // This will be cleared when the browser closes
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: captchaToken ? { captchaToken } : undefined,
    });

    if (error) {
      return { user: null, session: null, error };
    }

    // If rememberMe is false, we need to move the session to sessionStorage
    // so it's cleared when the browser closes
    if (!rememberMe && data.session) {
      // Get the current session from localStorage
      const authKey = 'candle-auth';
      const sessionData = localStorage.getItem(authKey);

      if (sessionData) {
        // Move to sessionStorage
        sessionStorage.setItem(authKey, sessionData);
        // Clear from localStorage
        localStorage.removeItem(authKey);
      }
    }

    return {
      user: data.user,
      session: data.session,
      error: null,
    };
  } catch (error) {
    return { user: null, session: null, error: error as Error };
  }
}

/**
 * Send password reset OTP to email
 * Uses regular email OTP (same as email sign-in)
 * After verification, user can update password
 */
export async function sendPasswordResetOTP(email: string): Promise<{ error: Error | null }> {
  try {

    // Use regular email OTP
    // This will work for existing users
    const { error } = await supabase.auth.signInWithOtp({
      email,
    });

    if (error) {
      console.error("[auth] sendPasswordResetOTP error:", error.message);
    }

    return { error };
  } catch (error) {
    console.error("[auth] sendPasswordResetOTP unexpected:", error);
    return { error: error as Error };
  }
}

/**
 * Reset password after OTP verification
 * Must be called after successful OTP verification
 */
export async function resetPassword(newPassword: string): Promise<{ error: Error | null }> {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      console.error("[auth] resetPassword error:", error.message);
      return { error };
    }

    return { error: null };
  } catch (error) {
    console.error("[auth] resetPassword unexpected:", error);
    return { error: error as Error };
  }
}

/**
 * Create or update user profile after OTP verification
 */
export async function upsertUserProfile(
  userId: string,
  data: {
    email?: string;
    phone?: string;
    fullname: string;
    country_code?: string;
  }
): Promise<{ error: Error | null }> {
  try {
    const { error } = await supabase.from('user_profile').upsert({
      user_id: userId,
      email: data.email || '',
      fullname: data.fullname,
      user_name: generateUsername(data.fullname),
      // Auth stores phone without '+'; persist E.164 with '+' on profile
      phone: normalizePhoneE164(data.phone) ?? data.phone,
      country_code: data.country_code,
      slug: generateUserSlug(data.fullname, userId),
    });

    return { error };
  } catch (error) {
    return { error: error as Error };
  }
}

/**
 * Send verification OTP to user's existing unverified email
 * For users who already have an email but haven't verified it
 */
export async function sendEmailVerificationOTP(): Promise<{ error: Error | null }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user?.email) {
      return { error: new Error('No email found on account') };
    }

    if (user.email_confirmed_at) {
      return { error: new Error('Email is already verified') };
    }

    // Send OTP to the user's email
    const { error } = await supabase.auth.signInWithOtp({
      email: user.email,
    });

    return { error };
  } catch (error) {
    return { error: error as Error };
  }
}

/**
 * Send verification OTP to user's existing unverified phone
 * For users who already have a phone but haven't verified it
 */
export async function sendPhoneVerificationOTP(): Promise<{ error: Error | null }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user?.phone) {
      return { error: new Error('No phone found on account') };
    }

    if (user.phone_confirmed_at) {
      return { error: new Error('Phone is already verified') };
    }

    // Send OTP to the user's phone
    const { error } = await supabase.auth.signInWithOtp({
      phone: user.phone,
    });

    return { error };
  } catch (error) {
    return { error: error as Error };
  }
}

/**
 * Verify email OTP for existing account
 * This verifies the email without creating a new session
 */
export async function verifyExistingEmailOTP(token: string): Promise<{ error: Error | null }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user?.email) {
      return { error: new Error('No email found on account') };
    }

    const { error } = await supabase.auth.verifyOtp({
      email: user.email,
      token,
      type: 'email',
    });

    return { error };
  } catch (error) {
    return { error: error as Error };
  }
}

/**
 * Verify phone OTP for existing account
 * This verifies the phone without creating a new session
 */
export async function verifyExistingPhoneOTP(token: string): Promise<{ error: Error | null }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user?.phone) {
      return { error: new Error('No phone found on account') };
    }

    const { error } = await supabase.auth.verifyOtp({
      phone: user.phone,
      token,
      type: 'sms',
    });

    return { error };
  } catch (error) {
    return { error: error as Error };
  }
}

const DEVICE_ID_STORAGE_KEY = 'candle-device-id';
let cachedDeviceId: string | null = null;

/**
 * Get or generate a persistent device ID for this browser profile.
 * Cached in memory so concurrent sign-in handlers cannot mint two IDs.
 */
export function getDeviceId(): string {
  if (cachedDeviceId) return cachedDeviceId;

  try {
    let deviceId = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (!deviceId) {
      deviceId = crypto.randomUUID
        ? crypto.randomUUID()
        : `device-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
    }
    cachedDeviceId = deviceId;
    return deviceId;
  } catch {
    cachedDeviceId =
      cachedDeviceId ??
      (crypto.randomUUID
        ? crypto.randomUUID()
        : `device-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`);
    return cachedDeviceId;
  }
}

/**
 * Parse JWT to extract session ID safely
 */
function getSessionIdFromJWT(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.session_id || null;
  } catch (e) {
    return null;
  }
}

/**
 * Register device after successful login
 */
export async function registerDevice(session: Session): Promise<void> {
  try {
    const device_fingerprint = getDeviceId();
    
    const ua = navigator.userAgent;
    let os_type = 'Unknown';
    if (/Windows NT/i.test(ua)) os_type = 'Windows';
    else if (/Mac OS X/i.test(ua)) os_type = 'macOS';
    else if (/Android/i.test(ua)) os_type = 'Android';
    else if (/iPhone|iPad/i.test(ua)) os_type = 'iOS';
    else if (/Linux/i.test(ua)) os_type = 'Linux';

    let device_model = 'Web Browser';
    if (/Edg/i.test(ua)) device_model = 'Edge Browser';
    else if (/Chrome/i.test(ua)) device_model = 'Chrome Browser';
    else if (/Firefox/i.test(ua)) device_model = 'Firefox Browser';
    else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) device_model = 'Safari Browser';

    const session_id = getSessionIdFromJWT(session.access_token);
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
    
    await fetch(`${SUPABASE_URL}/functions/v1/register-device`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        device_fingerprint,
        device_model,
        os_type,
        session_id
      })
    });
  } catch (error) {
    console.error('[auth] Failed to register device:', error);
  }
}
